/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Moving a card between columns, on a screen with no room to drag.
 *
 * Dragging is how the desktop board works and it cannot be what the phone
 * does: the columns are off-screen, so a drag would mean holding a card
 * against the edge and waiting for an auto-scroll to find the target — slow,
 * imprecise, and impossible with a screen reader. Tapping the stage opens
 * this, and the destination is a list.
 *
 * Every option carries a line explaining what the stage means. On the desktop
 * board the column headers do that work, and they are not here; without the
 * hints the sheet is five near-synonyms in a column and the reader has to
 * already know the funnel to use it.
 *
 * The sheet is presentation only — it takes options and calls back. The rules
 * about which move is legal live where they already lived, on the server and
 * in the page that owns the mutation.
 */

import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@/shared/ui/radix-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import { MobileBottomSheet } from './MobileBottomSheet';

export interface MoveStageOption {
  id: string;
  label: string;
  /** One line saying what this stage means; shown under the label. */
  hint?: string;
  /** The stage's own accent, as a CSS colour value. */
  color: string;
}

export interface MoveStageAction {
  id: string;
  label: string;
  /** Text colour, for the decisions that are not positions. */
  tone?: string;
  onSelect: () => void;
}

interface MoveStageSheetProps {
  open: boolean;
  /**
   * Overrides the default "Mover <name>" title.
   *
   * The attendance detail opens this sheet with no stages in it at all: its
   * stage is derived from recorded facts and nothing there is allowed to
   * push it, so the sheet carries only the three decisions. Calling that
   * "Mover" would promise a move it does not offer.
   */
  title?: string;
  /** Who the card is about — the sheet's title reads "Mover <name>". */
  subjectName: string;
  /** What it concerns: the property, shown under the title. */
  subjectDetail?: string;
  options: MoveStageOption[];
  /** The option the card sits in now; shown ticked and not re-selectable. */
  currentId: string;
  /** Decisions rather than positions: close, pause, abandon. */
  actions?: MoveStageAction[];
  onSelect: (optionId: string) => void;
  onClose: () => void;
}

export const MoveStageSheet: React.FC<MoveStageSheetProps> = ({
  open,
  title,
  subjectName,
  subjectDetail,
  options,
  currentId,
  actions,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation('features/attendances');

  return (
    <MobileBottomSheet
      open={open}
      title={title ?? t('move.title', { name: subjectName })}
      testId="move-stage-sheet"
      onClose={onClose}
      subtitle={subjectDetail ? (
        <Text
          data-testid="move-stage-subject"
          style={{
            margin: '0 var(--space-4) var(--space-3)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          {subjectDetail}
        </Text>
      ) : undefined}
    >
      <div role="list" style={{ display: 'flex', flexDirection: 'column' }}>
        {options.map((option) => {
          const isCurrent = option.id === currentId;
          return (
            <UnstyledButton
              key={option.id}
              role="listitem"
              data-testid={`move-stage-${option.id}`}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => (isCurrent ? onClose() : onSelect(option.id))}
              className="flex w-full items-center"
              style={{
                minHeight: 56,
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                marginBottom: 'var(--space-1)',
                borderRadius: 'var(--radius-md)',
                background: isCurrent
                  ? `color-mix(in srgb, ${option.color} 8%, transparent)`
                  : 'transparent',
                textAlign: 'left',
              }}
            >
              <Text
                component="span"
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  flexShrink: 0,
                  borderRadius: 'var(--radius-full)',
                  background: option.color,
                }}
              />
              <Text component="span" className="flex min-w-0 flex-1 flex-col" style={{ lineHeight: 1.35 }}>
                <Text
                  component="span"
                  style={{
                    fontSize: 'var(--text-body-sm)',
                    fontWeight: isCurrent ? 'var(--font-bold)' : 'var(--font-medium)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {option.label}
                </Text>
                {option.hint && (
                  <Text
                    component="span"
                    style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}
                  >
                    {option.hint}
                  </Text>
                )}
              </Text>
              {isCurrent && (
                <Text component="span" aria-hidden="true" style={{ color: option.color, flexShrink: 0 }}>
                  <CheckIcon width={16} height={16} />
                </Text>
              )}
            </UnstyledButton>
          );
        })}
      </div>

      {actions && actions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            marginTop: 'var(--space-3)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          {actions.map((action) => (
            <UnstyledButton
              key={action.id}
              data-testid={`move-stage-action-${action.id}`}
              onClick={action.onSelect}
              className="flex w-full items-center"
              style={{
                minHeight: 48,
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                color: action.tone ?? 'var(--text-primary)',
                fontSize: 'var(--text-body-sm)',
                fontWeight: 'var(--font-semibold)',
                textAlign: 'left',
              }}
            >
              {action.label}
            </UnstyledButton>
          ))}
        </div>
      )}
    </MobileBottomSheet>
  );
};

export default MoveStageSheet;
